import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LoadingIndicator from '../../../components/ui/LoadingIndicator';

const CompletedRenders = ({ renders = [], onDownload, onDelete, loading = false }) => {
  const [selectedRenders, setSelectedRenders] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  const formatTimeAgo = (date) => {
    if (!date) return 'Unknown';
    const now = new Date();
    const diffInHours = Math.floor((now - new Date(date)) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getQualityBadge = (quality) => {
    const styles = {
      ultra: 'bg-purple-100 text-purple-800 border-purple-200',
      high: 'bg-green-100 text-green-800 border-green-200',
      medium: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles?.[quality] || styles?.medium;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes?.[i]}`;
  };

  const handleSelectRender = (renderId) => {
    setSelectedRenders(prev => 
      prev?.includes(renderId) 
        ? prev?.filter(id => id !== renderId)
        : [...prev, renderId]
    );
  };

  const handleSelectAll = () => {
    setSelectedRenders(
      selectedRenders?.length === renders?.length 
        ? []
        : renders?.map(render => render?.id) || []
    );
  };

  const handleBulkDownload = () => {
    selectedRenders?.forEach(renderId => {
      const render = renders?.find(r => r?.id === renderId);
      if (render) onDownload?.(render);
    });
    setSelectedRenders([]);
  };

  const handleBulkDelete = () => {
    selectedRenders?.forEach(renderId => onDelete?.(renderId));
    setSelectedRenders([]);
  };

  // Sort renders
  const sortedRenders = React.useMemo(() => {
    if (!renders) return [];
    
    return [...renders]?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a?.name || '')?.localeCompare(b?.name || '');
        case 'size':
          return (b?.file_size || 0) - (a?.file_size || 0);
        case 'date':
        default:
          return new Date(b?.completed_at || 0) - new Date(a?.completed_at || 0);
      }
    });
  }, [renders, sortBy]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingIndicator type="spinner" size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Download" size={20} className="text-success" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Completed Renders</h2>
            <p className="text-sm text-muted-foreground">
              {renders?.length || 0} completed videos
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e?.target?.value)}
            className="text-sm border border-border rounded px-3 py-2 bg-background text-foreground"
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
          </select>
          
          {selectedRenders?.length > 0 && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Download"
                onClick={handleBulkDownload}
              >
                Download ({selectedRenders?.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                iconName="Trash2"
                onClick={handleBulkDelete}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {renders?.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedRenders?.length === renders?.length}
              onChange={handleSelectAll}
              className="rounded border-border"
            />
            <span className="text-sm text-foreground">
              Select all ({selectedRenders?.length} selected)
            </span>
          </label>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Download">
              Download All
            </Button>
            <Button variant="outline" size="sm" iconName="Share2">
              Share All
            </Button>
          </div>
        </div>
      )}

      {/* Renders Grid */}
      <div className="grid gap-4">
        {sortedRenders?.map((render) => (
          <div key={render?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start space-x-4">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedRenders?.includes(render?.id)}
                onChange={() => handleSelectRender(render?.id)}
                className="mt-1 rounded border-border"
              />

              {/* Thumbnail - placeholder since we don't have actual thumbnails */}
              <div className="w-20 h-11 bg-muted rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
                <Icon name="FileVideo" size={24} className="text-muted-foreground" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate mb-1">
                      {render?.name || 'Untitled Render'}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate mb-2">
                      {render?.project?.name || 'Unknown Project'}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>{render?.quality || 'Medium'} Quality</span>
                      {render?.video_file?.format && <span>{render?.video_file?.format}</span>}
                      {render?.video_file?.resolution && <span>{render?.video_file?.resolution}</span>}
                      {render?.video_file?.file_size && (
                        <span>{formatFileSize(render?.video_file?.file_size)}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {render?.quality && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getQualityBadge(render?.quality)}`}>
                        {render?.quality}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Completed {formatTimeAgo(render?.completed_at)}</span>
                    {render?.download_count > 0 && (
                      <span>Downloaded {render?.download_count} times</span>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {render?.output_path && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={() => onDownload?.(render)}
                      disabled={!render?.output_path}
                    >
                      Download
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Share2"
                    >
                      Share
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDelete?.(render?.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {/* Additional Info */}
                {render?.settings && Object.keys(render?.settings)?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <details className="text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Render Settings
                      </summary>
                      <pre className="mt-1 p-2 bg-muted/30 rounded overflow-x-auto text-foreground">
                        {JSON.stringify(render?.settings, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {(!renders || renders?.length === 0) && !loading && (
        <div className="text-center py-12">
          <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="text-sm font-medium text-foreground mb-1">No completed renders</h3>
          <p className="text-xs text-muted-foreground">Completed renders will appear here once processing is done</p>
        </div>
      )}
    </div>
  );
};

export default CompletedRenders;